export type AppSection = "home" | "qaida" | "quran" | "hifz" | "journey" | "classroom" | "resources" | "admin";

export type LearnerProgress = {
  completedQaida: string[];
  completedAyahs: string[];
  bookmarks: string[];
  points: number;
  streak: number;
  lastActive: string;
  lastRead?: { surah: number; ayah: number };
  weeklyGoal: number;
  /**
   * What live classes have covered, as "qaida:harakat" / "quran:2:255" keys.
   *
   * Kept apart from completedQaida rather than merged into it: attending a
   * lesson on a board is not the same claim as having practised it, and a
   * learner's own record should not quietly assert the stronger one.
   */
  classCovered: string[];
  /** Files a teacher released for study after class. */
  sharedResources: { key: string; title: string; sharedAt: string }[];
  /** Local days ("YYYY-MM-DD") the learner practised on, newest first — what the streak counts. */
  activeDays: string[];
  /** Ayahs marked practised per local day — what the weekly goal counts. */
  practiceLog: Record<string, number>;
  /**
   * The hifz method and its settings, chosen once and kept — so the Hifz page
   * opens on the learner's own plan on every device.
   */
  hifzPlan?: import("./lib/hifz-plan").HifzPlan;
};

export type QaidaCheck = {
  question: string;
  answers: string[];
  correct: number;
};

export type QaidaLesson = {
  id: string;
  number: number;
  title: string;
  arabicTitle: string;
  summary: string;
  teacherNote: string;
  objectives: string[];
  examples: { arabic: string; transliteration: string; note?: string }[];
  practice: string[];
  check: QaidaCheck;
};

export type QaidaExplorerCategory = "letters" | "joined" | "harakat" | "tanween" | "madd" | "leen" | "sukoon" | "shaddah" | "words";

export type QaidaExplorerItem = {
  id: string;
  category: QaidaExplorerCategory;
  arabic: string;
  nameEn: string;
  nameUr: string;
  transliteration: string;
  makhrajEn: string;
  makhrajUr: string;
  tajweedEn: string;
  tajweedUr: string;
  commonMistakeEn: string;
  commonMistakeUr: string;
  practiceEn: string;
  practiceUr: string;
};

export type QuranChapterSummary = {
  id: number;
  name: string;
  transliteration: string;
  translation: string;
  type: "meccan" | "medinan";
  total_verses: number;
};

export type QuranVerse = {
  id: number;
  text: string;
  translation: string;
  transliteration: string;
};

export type QuranChapter = QuranChapterSummary & { verses: QuranVerse[] };

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  role: "admin" | "manager" | "teacher" | "student" | "parent";
};

export const emptyProgress = (): LearnerProgress => ({
  completedQaida: [],
  completedAyahs: [],
  bookmarks: [],
  points: 0,
  streak: 0,
  lastActive: new Date().toISOString(),
  weeklyGoal: 5,
  classCovered: [],
  sharedResources: [],
  activeDays: [],
  practiceLog: {},
});
